import React from 'react';
import { Link, graphql } from 'gatsby';
import {
  Layout,
} from '../components';
import {
  Shape,
  Wrapper,
} from '../components/common';
import {
  PostList,
} from '../components/blog';
import {
  useMousePosition,
  useDimensions,
  useScrollPosition,
} from '../util/hooks';
import { isClient } from '../util/helpers';
import './blog.scss';

const Blog = (props) => {
  const {
    data: {
      site: {
        siteMetadata,
      },
      page: {
        data: pageData,
      },
      blog,
    },
    location,
  } = props;
  const {
    blogSlug,
  } = siteMetadata;
  const {
    title,
    subtitle,
    featuredCategories,
    metaTitle,
    metaDescription,
    openGraphImage,
  } = pageData;
  const seoData = {
    metaTitle,
    metaDescription,
    openGraphImage,
  };
  const pathPrefix = `/${blogSlug}`;
  const {
    x: cursorX,
    y: cursorY,
  } = useMousePosition();
  const {
    viewportWidth,
    viewportHeight,
    documentHeight,
  } = useDimensions();
  const viewportTopPos = useScrollPosition();
  const postItems = blog && blog.edges.map(post => post.node);
  const categoryItems = featuredCategories && featuredCategories.map(category => category.category.document[0]);
  categoryItems.forEach((category, index) => {
    categoryItems[index].posts = postItems.filter(post => category.uid === post.data.category.document[0].uid).slice(0, 3);
  });
  if (isClient) document.documentElement.style.setProperty('--bg-colour', '#FFFFF4');
  return (
    <Layout
      location={location}
      seoData={seoData}
      cursorX={cursorX}
      cursorY={cursorY}
      viewportWidth={viewportWidth}
      viewportHeight={viewportHeight}
      documentHeight={documentHeight}
      viewportTopPos={viewportTopPos}
    >
      <div className="blog-home">
        <section className="blog-hero">
          <Wrapper isBound>
            <div className="text-container">
              <h1 className="title">{title.text}</h1>
              <p className="subtitle">{subtitle.text}</p>
            </div>
          </Wrapper>
          <div className="framing-shapes">
            <Shape
              shapeName="flower"
              shapeColour="#2FCF00"
            />
            <Shape
              shapeName="petal"
              shapeColour="#0A85E5"
            />
            <Shape
              shapeName="circle"
              shapeColour="#FF6C13"
            />
            <Shape
              shapeName="leaf"
              shapeColour="#007307"
            />
          </div>
        </section>
        <div className="categories">
          <Wrapper isBound>
            {categoryItems && categoryItems.map((category) => {
              const {
                uid,
                data: categoryData,
                posts,
              } = category;
              const {
                categoryName,
              } = categoryData;
              if (!posts || posts.length < 1) return null;
              return (
                <section
                  className={`category-posts category-${uid}`}
                  key={uid}
                >
                  <div className="category-title">
                    <h2>{categoryName.text}</h2>
                    <Link
                      className="styled-link see-more-button"
                      to={`/${blogSlug}/${uid}/`}
                      aria-label={`See more posts in ${categoryName.text}`}
                    >
                      <span>See More</span>
                    </Link>
                  </div>
                  <PostList
                    posts={posts}
                    siteMetadata={siteMetadata}
                    pathPrefix={pathPrefix}
                  />
                </section>
              );
            })}
          </Wrapper>
        </div>
      </div>
    </Layout>
  );
};

export default Blog;

export const pageQuery = graphql`
  query BlogPageQuery {
    site {
      siteMetadata {
        blogSlug,
      }
    },
    page: prismicBlog {
      uid,
      data {
        title {
          text
        }
        subtitle {
          text
        }
        featuredCategories: featured_categories {
          category {
            document {
              uid
              data {
                categoryName: category_name {
                  text
                }
              }
            }
          }
        }
        metaTitle: meta_title {
          html
          text
        },
        metaDescription: meta_description {
          html
          text
        },
        openGraphImage: open_graph_image {
          alt
          copyright
          url
        }
      }
    }
    blog: allPrismicBlogPost(
      sort: { fields: [data___date], order: DESC }
    ) {
      edges {
        node {
          uid
          data {
            postTitle: post_title {
              text
            }
            featuredImage: featured_image {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1200, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
              alt
            }
            date(formatString: "DD MMM YYYY")
            category {
              document {
                uid
              }
            }
            author {
              document {
                data {
                  authorName: author_name {
                    text
                  }
                }
              }
            }
            metaDescription: meta_description {
              html
              text
            },
          }
        }
      }
    }
  }
`;
